import React from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  TextInput,
  PasswordInput,
  Button,
  Container,
  Alert,
  Anchor,
  Title,
  Stack,
  Image,
  Box,
  Paper,
  Text,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle } from '@tabler/icons-react';
import { useAuthStore } from '../../../application/store/authStore';
import { LoginCommand } from '../../../application/dto/auth/LoginCommand';
import { PUBLIC_ROUTES } from '../../routing/routes';
import LogoSvg from '../../../assets/images/logo.svg';

/**
 * LoginPage — the MineMiner SPA's only auth surface.
 *
 * sprint-104 (epic-013 Sprint B, step 2d / S5). Forked from `Frontend/`'s
 * LoginPage and trimmed:
 *
 *   - Calls the carved `POST /api/mineminer/auth/login` via authStore.login()
 *     (the AUTH.LOGIN endpoint is repointed in `infrastructure/api/config.ts`).
 *   - No registration link (the carved host has no register endpoint).
 *
 * sprint-107 S1.5 (epic-013 Sprint D — Stream A item A2): the carved host
 * now exposes a forgot-password / reset-password surface; the link below
 * routes to the new `/forgot-password` page.
 *
 * sprint-126 (epic-019 Sprint E): on success navigate to `/` so `HomeRedirect`
 * is the single source of truth for the authenticated landing. Previously this
 * hardcoded `MINEMINER_ROUTES.BASE`, which bypassed `HomeRedirect` and defeated
 * the sprint-124 post-login → Elko redirect for the primary login-form path. The
 * post-Elko revert is now genuinely one line in `HomeRedirect` (TECH_DEBT #127).
 */
/**
 * Accepts only an app-relative path, so `returnTo` can never send a user off-site
 * (`//host`, `/\host` and absolute URLs all fall back to `/`).
 */
export const safeReturnTo = (value: string | null): string =>
  value && value.startsWith('/') && !value.startsWith('//') && !value.startsWith('/\\')
    ? value
    : '/';

export const LoginPage: React.FC = (): React.ReactElement => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const login = useAuthStore((state) => state.login);
  const isLoading = useAuthStore((state) => state.isLoading);
  const error = useAuthStore((state) => state.error);
  const clearError = useAuthStore((state) => state.clearError);

  const form = useForm<LoginCommand>({
    initialValues: {
      userName: '',
      password: '',
    },
    validate: {
      userName: (value: string): string | null => {
        if (!value || value.trim().length === 0) {
          return 'Email is required';
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          return 'Please enter a valid email address';
        }
        return null;
      },
      password: (value: string): string | null => {
        if (!value || value.trim().length === 0) {
          return 'Password is required';
        }
        if (value.length < 8) {
          return 'Password must be at least 8 characters';
        }
        return null;
      },
    },
  });

  const handleSubmit = async (values: LoginCommand): Promise<void> => {
    clearError();
    try {
      await login(values);
      // Navigate to the apex route; HomeRedirect decides the authenticated
      // landing (Elko event during the show; reverts to BASE post-Elko, #127).
      // A same-site `returnTo` (set by ProtectedRoute) takes precedence.
      navigate(safeReturnTo(searchParams.get('returnTo')));
    } catch {
      // Error state is in the auth store; the Alert below renders it.
    }
  };

  return (
    <Box mih="100vh" bg="gray.0" py={80} px="md">
      <Container size="xs">
        <Paper bg="white" p={48} radius="md" shadow="sm" withBorder>
          <Stack gap="md" align="center" mb="md">
            <Image src={LogoSvg} alt="MineMiner" h={48} w="auto" />
            <Title order={1} size="h2" ta="center">
              Sign in to MineMiner
            </Title>
            <Text size="sm" c="dimmed" ta="center">
              MSHA mine-safety leads and CRM.
            </Text>
          </Stack>

          {error !== null && error !== '' && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              color="red"
              variant="light"
              mb="md"
            >
              {error}
            </Alert>
          )}

          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack gap="md">
              <TextInput
                label="Email"
                type="email"
                placeholder="your@email.com"
                required
                size="md"
                {...form.getInputProps('userName')}
              />

              <PasswordInput
                label="Password"
                placeholder="Your password"
                required
                size="md"
                {...form.getInputProps('password')}
              />

              <Button
                type="submit"
                size="lg"
                variant="filled"
                color="brand"
                disabled={!form.isValid() || isLoading}
                loading={isLoading}
                fullWidth
              >
                {isLoading ? 'Signing in...' : 'Sign In'}
              </Button>

              <Text ta="center" size="sm" mt="xs">
                <Anchor component={Link} to={PUBLIC_ROUTES.FORGOT_PASSWORD} fw={500}>
                  Forgot password?
                </Anchor>
              </Text>
            </Stack>
          </form>
        </Paper>
      </Container>
    </Box>
  );
};
